import { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { BrandMark } from "@/components/Logo";
import PublicSite from "@/marketing/PublicSite";
import AppShell from "@/app/AppShell";
import Dashboard from "@/app/Dashboard";
import InviteLanding from "@/app/InviteLanding";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const ACTIVE_KEY = "redvolt_active_tenant";
const INVITE_KEY = "redvolt_pending_invite";

// Capture an ?invite=<token> from the URL, persist it across the WorkOS
// sign-in round-trip, and strip it from the address bar.
const captureInvite = () => {
  const params = new URLSearchParams(window.location.search);
  const tok = params.get("invite");
  if (tok) {
    localStorage.setItem(INVITE_KEY, tok);
    params.delete("invite");
    const qs = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }
  return localStorage.getItem(INVITE_KEY);
};

const BrandedLoader = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
    <div className="animate-pulse">
      <BrandMark size={44} />
    </div>
    <p className="text-sm text-slate-500">Loading RedVolt…</p>
  </div>
);

const AuthedApp = ({ me, onSignOut, onReload, initialView = "home" }) => {
  const memberships = useMemo(() => me.memberships || [], [me.memberships]);
  const [activeTenant, setActiveTenant] = useState(
    () => localStorage.getItem(ACTIVE_KEY) || memberships[0]?.tenant_id || null
  );
  const [view, setView] = useState(initialView);

  // Keep active tenant valid against current memberships.
  useEffect(() => {
    if (memberships.length === 0) return;
    const valid = memberships.some((m) => m.tenant_id === activeTenant);
    if (!valid) setActiveTenant(memberships[0].tenant_id);
  }, [memberships, activeTenant]);

  useEffect(() => {
    if (activeTenant) localStorage.setItem(ACTIVE_KEY, activeTenant);
  }, [activeTenant]);

  const active = memberships.find((m) => m.tenant_id === activeTenant) || memberships[0] || null;

  const onSwitch = useCallback((tenantId) => {
    setActiveTenant(tenantId);
    setView("home");
  }, []);

  const onCreateOrg = useCallback(async (payload) => {
    const r = await api.post("/organizations", payload);
    const id = r.data.id;
    // Refresh memberships FIRST so the new org is a known-valid membership before
    // we make it active — otherwise the "keep active tenant valid" guard resets it.
    await onReload();
    localStorage.setItem(ACTIVE_KEY, id);
    setActiveTenant(id);
    setView("home");
    toast.success(`${r.data.name} created — you're now its Owner.`);
    return r.data;
  }, [onReload]);

  return (
    <AppShell
      me={me}
      memberships={memberships}
      active={active}
      activeView={view}
      onNavigate={setView}
      onSwitch={onSwitch}
      onSignOut={onSignOut}
      api={api}
      onCreateOrg={onCreateOrg}
    >
      <Dashboard
        api={api}
        me={me}
        memberships={memberships}
        active={active}
        view={view}
        onNavigate={setView}
        onReload={onReload}
        onSignOut={onSignOut}
      />
    </AppShell>
  );
};

const Shell = ({ initialView }) => {
  const [me, setMe] = useState(undefined);
  const [pendingInvite, setPendingInvite] = useState(() => captureInvite());
  const [accepting, setAccepting] = useState(false);

  const loadMe = useCallback(() => {
    return api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  // Once authenticated, redeem any pending invitation into the correct org+role.
  useEffect(() => {
    if (!me || !pendingInvite || accepting) return;
    setAccepting(true);
    (async () => {
      try {
        const r = await api.post("/invitations/accept", { token: pendingInvite });
        localStorage.setItem(ACTIVE_KEY, r.data.tenant_id);
        localStorage.removeItem(INVITE_KEY);
        setPendingInvite(null);
        toast.success("Invitation accepted — welcome aboard!");
        loadMe();
      } catch (e) {
        const code = e?.response?.data?.detail;
        let msg = "This invitation is invalid or has already expired.";
        if (code === "invitation_email_mismatch") {
          let invited = "";
          try {
            const p = await api.get(`/invitations/${encodeURIComponent(pendingInvite)}/preview`);
            invited = p.data.email;
          } catch { /* preview may fail if expired */ }
          msg = invited
            ? `This invitation was sent to ${invited}, but you're signed in as ${me.user.email}. Sign out and sign back in using ${invited} to accept.`
            : `This invitation was sent to a different email than ${me.user.email}. Sign in with the invited address to accept.`;
        }
        localStorage.removeItem(INVITE_KEY);
        setPendingInvite(null);
        toast.error(msg, { duration: 14000 });
      } finally {
        setAccepting(false);
      }
    })();
  }, [me, pendingInvite, accepting, loadMe]);

  const signIn = (hint) => {
    const params = new URLSearchParams();
    if (hint === "sign-up" || hint === "sign-in") params.set("screen_hint", hint);
    if (pendingInvite) params.set("invite", pendingInvite);
    const q = params.toString();
    window.location.href = `${API}/auth/login${q ? `?${q}` : ""}`;
  };

  const dismissInvite = () => {
    localStorage.removeItem(INVITE_KEY);
    setPendingInvite(null);
  };

  const signOut = async () => {
    try {
      const r = await api.post("/auth/logout");
      window.location.href = r.data.logout_url || "/";
    } catch {
      window.location.href = "/";
    }
  };

  if (me === undefined) return <BrandedLoader />;
  if (!me) {
    if (pendingInvite) return <InviteLanding onSignIn={signIn} onDismiss={dismissInvite} />;
    return <PublicSite onSignIn={signIn} />;
  }
  return <AuthedApp me={me} onSignOut={signOut} onReload={loadMe} initialView={initialView} />;
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Shell />} />
          <Route path="/admin" element={<Shell initialView="admin" />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
